import { CheckboxResult } from '../CheckboxResult/CheckboxResult';
import { EmojiChoiceResult } from '../EmojiChoiceResult/EmojiChoiceResult';
import { MultiChoiceResult } from '../MultiChoiceResult/MultiChoiceResult';
import { PlainTextResult } from '../PlainTextResult/PlainTextResult';
import { RankingQuestionResult } from '../RankingQuestionResult/RankingQuestionResult';
import { RatingResult } from '../RatingResult/RatingResult';
import { YesNoResult } from '../YesNoResult/YesNoResults';

import type { TextAnswer } from './types';
import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionType } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getResultEmojiSrc } from '@/utils/emojis';
import { getLocalizedText } from '@/utils/localization';

interface AnswerResultProps {
    question: ReviewQuestion;
    answerData?: Record<string | number, number>;
    answeredCount?: number;
    textAnswers?: TextAnswer[];
    dataTestId?: string;
    className?: string;
    reviewee?: User;
}

export const AnswerResult = ({
    question,
    answerData,
    answeredCount,
    textAnswers,
    dataTestId = 'answer-result',
    className = '',
    reviewee,
}: AnswerResultProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const renderOptionLabel = (label: Record<string, string> | undefined): string =>
        replaceDynamicTags({ text: getLocalizedText(label), employee: reviewee });

    if (!answerData && !textAnswers?.length) {
        return null;
    }

    switch (question.questionType) {
        case QuestionType.RATING_SCALE: {
            if (!question.reviewQuestionOptions || !answerData) {
                return null;
            }

            const sortedOptions = [...question.reviewQuestionOptions].sort(
                (a, b) => a.sortOrder - b.sortOrder,
            );

            const ratingAnswers = Object.entries(answerData).map(([value, count]) => ({
                value: Number(value),
                count: Number(count),
            }));

            return (
                <RatingResult
                    from={sortedOptions[0]?.scoreValue ?? 1}
                    to={sortedOptions[1]?.scoreValue ?? 7}
                    answers={ratingAnswers}
                    startLabel={
                        renderOptionLabel(sortedOptions[0]?.label) || t('common.stronglyDisagree')
                    }
                    endLabel={
                        renderOptionLabel(sortedOptions[1]?.label) || t('common.stronglyAgree')
                    }
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-rating-result`}
                />
            );
        }

        case QuestionType.EMOJI_CHOICE: {
            if (!answerData) {
                return null;
            }

            const optionsCount = question.reviewQuestionOptions?.length ?? 0;

            const emojiOptions =
                question.reviewQuestionOptions?.map((option, index) => ({
                    id: `emoji-${index}`,
                    src: getResultEmojiSrc(index, optionsCount),
                    label: renderOptionLabel(option.label),
                    count: answerData[option.id ?? ''] ?? 0,
                })) ?? [];

            return (
                <EmojiChoiceResult
                    options={emojiOptions}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-emoji-choice-result`}
                />
            );
        }

        case QuestionType.CHECKBOX: {
            if (!answerData) {
                return null;
            }

            return (
                <CheckboxResult
                    question={question}
                    answerData={answerData}
                    answeredCount={answeredCount}
                    textAnswers={textAnswers}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-checkbox-result`}
                    reviewee={reviewee}
                />
            );
        }

        case QuestionType.MULTI_CHOICE: {
            if (!answerData) {
                return null;
            }

            return (
                <MultiChoiceResult
                    question={question}
                    answerData={answerData}
                    answeredCount={answeredCount}
                    textAnswers={textAnswers}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-multi-choice-result`}
                    reviewee={reviewee}
                />
            );
        }

        case QuestionType.YES_NO: {
            if (!answerData) {
                return null;
            }

            const total = Object.values(answerData).reduce((sum, val) => sum + val, 0);
            const yesCount = answerData[0] ?? 0;
            const noCount = answerData[1] ?? 0;

            const yesPercentage = total > 0 ? Math.round((yesCount / total) * 100) : 0;
            const noPercentage = total > 0 ? Math.round((noCount / total) * 100) : 0;

            return (
                <YesNoResult
                    yesPercentage={yesPercentage}
                    noPercentage={noPercentage}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-yes-no-result`}
                />
            );
        }

        case QuestionType.RANKING: {
            if (!answerData) {
                return null;
            }

            const rankingOptions =
                question.reviewQuestionOptions?.map((option) => ({
                    label: renderOptionLabel(option.label),
                    count: answerData[option.id ?? ''] ?? 0,
                })) ?? [];

            return (
                <RankingQuestionResult
                    options={rankingOptions}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-ranking`}
                />
            );
        }

        case QuestionType.PLAIN_TEXT:
            return (
                <PlainTextResult
                    answers={textAnswers ?? []}
                    className={`mt-4 ${className}`}
                    dataTestId={`${dataTestId}-plain-text-result`}
                />
            );
        case QuestionType.SECTION_TITLE:
        default:
            return null;
    }
};
